import { useEffect, useMemo, useState } from "react";
import { authFetch } from "../lib/api";
import { showToast } from "../lib/toast";
import {
  AlignLeft,
  ArrowLeft,
  Brain,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Copy,
  Download,
  Eye,
  Hash,
  Loader2,
  Mail,
  Pencil,
  Plus,
  QrCode,
  Star,
  ToggleLeft,
  Trash2,
  Type,
  Users,
  X,
} from "lucide-react";
import { KB_ROLE_LEVELS } from "../portals/RAGChat";
import { WizardShell } from "../components/WizardShell";

const SURVEY_QTYPES = [
  { id: "short_text", label: "Short text", icon: Type },
  { id: "long_text", label: "Paragraph", icon: AlignLeft },
  { id: "single_select", label: "Single choice", icon: ClipboardList },
  { id: "multi_select", label: "Multiple choice", icon: ClipboardList },
  { id: "boolean", label: "Yes / No", icon: ToggleLeft },
  { id: "scale", label: "Scale 1–5", icon: Star },
  { id: "number", label: "Number", icon: Hash },
];
const SURVEY_DOC_TYPES = ["position", "faq", "event", "legislative"];
const SURVEY_AUDIENCES = [
  {
    id: "candidate",
    label: "Candidate",
    hint: "Email-invited; approved answers sync to KB",
  },
  {
    id: "constituent",
    label: "Constituent",
    hint: "Public QR / link; canvasser-attributed",
  },
  {
    id: "user",
    label: "User",
    hint: "Your FieldIQ users; editable while published",
  },
];
const SURVEY_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  published: "bg-green-100 text-green-700",
  closed: "bg-red-100 text-red-600",
  scheduled: "bg-amber-100 text-amber-700",
  ended: "bg-slate-100 text-slate-500",
};

// Natural run length differs by audience: a user check-in matches the monthly
// cadence, a candidate survey needs time to chase non-responders, and a
// canvass campaign runs for a season. Suggested only — always editable, and
// clearable to "no deadline".
const SURVEY_DEFAULT_DAYS = { candidate: 30, constituent: 90, user: 30 };

const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// A picked day means the whole day in the admin's own timezone: opens at
// midnight, closes at the end of it.
const fromDateInput = (value, endOfDay) =>
  value
    ? new Date(`${value}T${endOfDay ? "23:59:59" : "00:00:00"}`).toISOString()
    : null;

const defaultCloseDate = (audience) => {
  const d = new Date();
  d.setDate(d.getDate() + (SURVEY_DEFAULT_DAYS[audience] ?? 30));
  return toDateInput(d.toISOString());
};

// A published survey outside its window must not still read as "published".
const effectiveSurveyStatus = (s) => {
  if (s.status !== "published") return s.status;
  if (s.is_collecting !== false) return "published";
  if (s.opens_at && new Date(s.opens_at).getTime() > Date.now())
    return "scheduled";
  return "ended";
};
let _sqIdx = 0;
const surveyUid = () => `q${++_sqIdx}_${Date.now()}`;
const newSurveyQuestion = () => ({
  id: surveyUid(),
  type: "short_text",
  label: "",
  topic: "",
  required: false,
  options: [],
  kbInclude: true,
  doc_type: "position",
});

const SurveyList = ({ facility, kbRole, onEdit, onResponses, onNew }) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(null);
  const fid = facility.facility_id;
  const canEdit =
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS.editor ?? 99);

  useEffect(() => {
    authFetch(`/surveys?facility_id=${fid}`)
      .then((d) => setSurveys(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fid]);

  // Closing is what ends a user survey's editable window, so it needs to be
  // reachable without opening the wizard.
  const setStatus = async (survey, status) => {
    setStatusBusy(survey.id);
    const res = await authFetch(`/surveys/${survey.id}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
    setStatusBusy(null);
    if (res?.error) {
      showToast(res.error);
      return;
    }
    setSurveys((prev) =>
      prev.map((s) => (s.id === survey.id ? { ...s, status } : s)),
    );
  };

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Surveys</h2>
        {canEdit && (
          <button
            onClick={onNew}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Survey
          </button>
        )}
      </div>
      {surveys.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-500">No surveys yet</p>
          {canEdit && (
            <p className="text-sm text-slate-400 mt-1">
              Create your first survey to get started.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {[
                  "Title",
                  "Audience",
                  "Status",
                  "Responses",
                  "Created",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left py-3 px-4 font-medium text-slate-600"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {surveys.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4 font-medium text-slate-800">
                    {s.title || (
                      <span className="text-slate-400 italic">Untitled</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-600 capitalize">
                    {s.audience}
                  </td>
                  <td className="py-3 px-4">
                    {(() => {
                      const eff = effectiveSurveyStatus(s);
                      const when =
                        eff === "scheduled"
                          ? `Opens ${new Date(s.opens_at).toLocaleDateString()}`
                          : eff === "published" && s.closes_at
                            ? `Closes ${new Date(s.closes_at).toLocaleDateString()}`
                            : eff === "ended" && s.closes_at
                              ? `Closed ${new Date(s.closes_at).toLocaleDateString()}`
                              : null;
                      return (
                        <>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                              SURVEY_STATUS_COLORS[eff] ||
                              "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {eff}
                          </span>
                          {when && (
                            <div className="text-xs text-slate-400 mt-1">
                              {when}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {s.response_count ?? 0}
                  </td>
                  <td className="py-3 px-4 text-slate-500">
                    {new Date(s.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2 justify-end items-center">
                      {canEdit && s.status !== "draft" && (
                        <button
                          onClick={() =>
                            setStatus(
                              s,
                              s.status === "published" ? "closed" : "published",
                            )
                          }
                          disabled={statusBusy === s.id}
                          className="px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
                          title={
                            s.status === "published"
                              ? "Close — locks answers and clears the FieldIQ alert"
                              : "Reopen for responses"
                          }
                        >
                          {statusBusy === s.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : s.status === "published" ? (
                            "Close"
                          ) : (
                            "Reopen"
                          )}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => onEdit(s)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onResponses(s)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="View Responses"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SurveyEditor = ({ facility, kbRole, surveyId, onBack }) => {
  const fid = facility.facility_id;
  const [survey, setSurvey] = useState(null);
  const [localTitle, setLocalTitle] = useState("");
  const [localAudience, setLocalAudience] = useState("candidate");
  const [localQuestions, setLocalQuestions] = useState([newSurveyQuestion()]);
  const [expandedQ, setExpandedQ] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(!!surveyId);
  const [emailsText, setEmailsText] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [err, setErr] = useState("");
  // Collection window. Empty string = unset; opens empty means "as soon as
  // published", closes empty means "until I close it myself".
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState(() =>
    surveyId ? "" : defaultCloseDate("candidate"),
  );
  // Once the admin edits the close date, stop re-suggesting on audience change.
  const [closeTouched, setCloseTouched] = useState(false);

  const canPublish =
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS.editor ?? 99);
  const isLocked = survey?.status === "closed";
  const isDraft = !survey || survey.status === "draft";
  const isPublished = survey?.status === "published";

  useEffect(() => {
    if (!surveyId) return;
    authFetch(`/surveys/${surveyId}`)
      .then((d) => {
        if (!d || d.error) return;
        setSurvey(d);
        setLocalTitle(d.title || "");
        setLocalAudience(d.audience || "candidate");
        setLocalQuestions(d.schema?.length ? d.schema : [newSurveyQuestion()]);
        setOpensAt(toDateInput(d.opens_at));
        setClosesAt(toDateInput(d.closes_at));
        setCloseTouched(true);
      })
      .finally(() => setLoading(false));
  }, [surveyId]);

  // The survey is created lazily: first step-change with a title POSTs the
  // draft, later changes PUT it, so abandoning the wizard on step 1 creates
  // nothing (the old flow minted a blank survey on every New Survey click).
  const persistDraft = async () => {
    if (isLocked || !localTitle.trim()) return survey;
    setSaving(true);
    setErr("");
    const body = {
      title: localTitle,
      audience: localAudience,
      schema: localQuestions,
      opens_at: fromDateInput(opensAt, false),
      closes_at: fromDateInput(closesAt, true),
    };
    const sid = survey?.id ?? surveyId;
    const res = await authFetch(sid ? `/surveys/${sid}` : "/surveys", {
      method: sid ? "PUT" : "POST",
      body: JSON.stringify(sid ? body : { ...body, facility_id: fid }),
    });
    setSaving(false);
    if (res?.error) {
      setErr(res.error);
      return null;
    }
    setSurvey(res);
    return res;
  };

  // Finish button: publishes a draft (after a final save), then flips to
  // "Done" which just closes. Non-publishers only ever get the close path.
  const finish = async () => {
    setErr("");
    if (!isDraft || !canPublish) {
      if (isDraft) await persistDraft();
      onBack();
      return;
    }
    const saved = await persistDraft();
    if (!saved?.id) return;
    setPublishing(true);
    const emails = emailsText
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const res = await authFetch(`/surveys/${saved.id}/publish`, {
      method: "POST",
      body: JSON.stringify(localAudience === "candidate" ? { emails } : {}),
    });
    setPublishing(false);
    if (res?.error) {
      setErr(res.error);
      return;
    }
    setSurvey((prev) => ({ ...(prev || saved), status: "published" }));
    if (localAudience === "candidate") {
      setInviteResult(res);
      setEmailsText("");
    }
    // Constituent and user surveys have no link to hand back: constituent
    // responses come from single-use tokens minted in FieldIQ, and user
    // surveys surface automatically. Publishing is the distribution step.
  };

  const close = async () => {
    if (isDraft) await persistDraft();
    onBack();
  };

  const handleInvite = async () => {
    setPublishing(true);
    setErr("");
    const emails = emailsText
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const res = await authFetch(`/surveys/${survey.id}/invite`, {
      method: "POST",
      body: JSON.stringify({ emails }),
    });
    setPublishing(false);
    if (res?.error) {
      setErr(res.error);
    } else {
      setInviteResult(res);
      setEmailsText("");
    }
  };

  const toggleQ = (id) =>
    setExpandedQ((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const updateQ = (id, patch) =>
    setLocalQuestions((qs) =>
      qs.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    );

  const removeQ = (id) =>
    setLocalQuestions((qs) => qs.filter((q) => q.id !== id));

  const addQ = () => {
    const q = newSurveyQuestion();
    setLocalQuestions((qs) => [...qs, q]);
    setExpandedQ((prev) => new Set([...prev, q.id]));
  };

  const moveQ = (idx, dir) =>
    setLocalQuestions((qs) => {
      const n = [...qs];
      const t = idx + dir;
      if (t < 0 || t >= n.length) return n;
      [n[idx], n[t]] = [n[t], n[idx]];
      return n;
    });

  const kbPreview = useMemo(
    () =>
      localAudience !== "candidate"
        ? []
        : localQuestions
            .filter((q) => q.kbInclude && q.label)
            .map((q) => ({
              label: q.label,
              doc_type: q.doc_type || "position",
            })),
    [localQuestions, localAudience],
  );

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );

  const steps = [
    {
      key: "basics",
      title: "Basics",
      nextDisabled: !localTitle.trim(),
      content: (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Title</p>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
              placeholder="Survey title…"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              disabled={isLocked}
            />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Audience</p>
            <div className="grid grid-cols-3 gap-3">
              {SURVEY_AUDIENCES.map(({ id, label, hint }) => (
                <button
                  key={id}
                  onClick={() => {
                    if (isLocked || isPublished) return;
                    setLocalAudience(id);
                    // Re-suggest the window only while it is still a suggestion.
                    if (!closeTouched) setClosesAt(defaultCloseDate(id));
                  }}
                  disabled={isLocked || isPublished}
                  className={`p-3 rounded-lg border-2 text-left transition-colors disabled:cursor-not-allowed ${
                    localAudience === id
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="font-medium text-slate-800">{label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-700 mb-1">Validity</p>
            <p className="text-xs text-slate-400 mb-3">
              Responses are only accepted inside this window. Leave a date empty
              for no limit.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Opens
                </label>
                <input
                  type="date"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                  value={opensAt}
                  onChange={(e) => setOpensAt(e.target.value)}
                  disabled={isLocked}
                />
                <p className="text-xs text-slate-400 mt-1">
                  {opensAt ? "Opens at midnight" : "As soon as you publish"}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Closes
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                    value={closesAt}
                    min={opensAt || undefined}
                    onChange={(e) => {
                      setClosesAt(e.target.value);
                      setCloseTouched(true);
                    }}
                    disabled={isLocked}
                  />
                  {closesAt && !isLocked && (
                    <button
                      onClick={() => {
                        setClosesAt("");
                        setCloseTouched(true);
                      }}
                      className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0"
                      title="No deadline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {closesAt
                    ? "Closes at the end of that day"
                    : "Runs until you close it"}
                </p>
              </div>
            </div>
            {localAudience === "user" && (
              <p className="text-xs text-slate-400 mt-3">
                A close date suits a recurring check-in — the survey stops
                accepting edits on its own, so next month's can replace it.
              </p>
            )}
            {localAudience === "constituent" && (
              <p className="text-xs text-slate-400 mt-3">
                Separate from this: each canvasser-issued link expires 7 days
                after it is created, however long the survey runs.
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "questions",
      title: "Questions",
      nextDisabled: !localQuestions.some((q) => q.label.trim()),
      content: (
        <div>
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <p className="font-medium text-slate-700">Questions</p>
              {!isLocked && (
                <button
                  onClick={addQ}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add question
                </button>
              )}
            </div>
            {localQuestions.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                No questions — add one above.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {localQuestions.map((q, idx) => {
                  const isExp = expandedQ.has(q.id);
                  const QIcon =
                    SURVEY_QTYPES.find((t) => t.id === q.type)?.icon || Type;
                  return (
                    <div key={q.id} className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveQ(idx, -1)}
                            disabled={idx === 0 || isLocked}
                            className="text-slate-300 hover:text-slate-500 disabled:opacity-20"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => moveQ(idx, 1)}
                            disabled={
                              idx === localQuestions.length - 1 || isLocked
                            }
                            className="text-slate-300 hover:text-slate-500 disabled:opacity-20"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <QIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 text-sm text-slate-700 truncate">
                          {q.label || (
                            <span className="text-slate-400 italic">
                              Question {idx + 1}
                            </span>
                          )}
                        </span>
                        {q.required && (
                          <span className="text-xs text-red-500 font-medium">
                            Required
                          </span>
                        )}
                        {!isLocked && (
                          <button
                            onClick={() => removeQ(q.id)}
                            className="p-1 text-slate-300 hover:text-red-400 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleQ(q.id)}
                          className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        >
                          {isExp ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      {isExp && (
                        <div className="mt-3 space-y-3 pl-10">
                          <textarea
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                            rows={2}
                            placeholder="Question label…"
                            value={q.label}
                            onChange={(e) =>
                              updateQ(q.id, { label: e.target.value })
                            }
                            disabled={isLocked}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">
                                Type
                              </label>
                              <select
                                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                                value={q.type}
                                onChange={(e) =>
                                  updateQ(q.id, {
                                    type: e.target.value,
                                    options: [
                                      "single_select",
                                      "multi_select",
                                    ].includes(e.target.value)
                                      ? q.options?.length
                                        ? q.options
                                        : [""]
                                      : [],
                                  })
                                }
                                disabled={isLocked}
                              >
                                {SURVEY_QTYPES.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">
                                Topic
                              </label>
                              <input
                                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                                placeholder="e.g. Economy"
                                value={q.topic}
                                onChange={(e) =>
                                  updateQ(q.id, { topic: e.target.value })
                                }
                                disabled={isLocked}
                              />
                            </div>
                          </div>
                          {["single_select", "multi_select"].includes(
                            q.type,
                          ) && (
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">
                                Options
                              </label>
                              {(q.options || []).map((opt, oi) => (
                                <div
                                  key={oi}
                                  className="flex items-center gap-2 mb-1"
                                >
                                  <input
                                    className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                                    value={opt}
                                    placeholder={`Option ${oi + 1}`}
                                    onChange={(e) => {
                                      const opts = [...q.options];
                                      opts[oi] = e.target.value;
                                      updateQ(q.id, { options: opts });
                                    }}
                                    disabled={isLocked}
                                  />
                                  {!isLocked && (
                                    <button
                                      onClick={() =>
                                        updateQ(q.id, {
                                          options: q.options.filter(
                                            (_, i) => i !== oi,
                                          ),
                                        })
                                      }
                                      className="text-slate-300 hover:text-red-400"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {!isLocked && (
                                <button
                                  onClick={() =>
                                    updateQ(q.id, {
                                      options: [...(q.options || []), ""],
                                    })
                                  }
                                  className="text-blue-600 text-xs hover:text-blue-700 mt-1"
                                >
                                  + Add option
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-4 flex-wrap">
                            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={q.required}
                                onChange={(e) =>
                                  updateQ(q.id, { required: e.target.checked })
                                }
                                disabled={isLocked}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-300"
                              />
                              Required
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ),
    },
    ...(localAudience === "candidate"
      ? [
          {
            key: "kb",
            title: "KB Mapping",
            content: (
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                <p className="font-medium text-slate-700 flex items-center gap-1.5">
                  <Brain className="w-4 h-4 text-blue-500" />
                  Knowledge base mapping
                </p>
                <p className="text-xs text-slate-400">
                  Approved answers to checked questions sync to the knowledge
                  base as pending_review rows. Pick a doc type for each.
                </p>
                {localQuestions.filter((q) => q.label.trim()).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">
                    No labeled questions yet — add them in the Questions step.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {localQuestions
                      .filter((q) => q.label.trim())
                      .map((q) => (
                        <div
                          key={q.id}
                          className="flex items-center gap-3 border border-slate-100 rounded-lg p-2.5"
                        >
                          <input
                            type="checkbox"
                            checked={q.kbInclude}
                            onChange={(e) =>
                              updateQ(q.id, { kbInclude: e.target.checked })
                            }
                            disabled={isLocked}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-300"
                          />
                          <span className="flex-1 text-sm text-slate-700 truncate">
                            {q.label}
                          </span>
                          {q.kbInclude && (
                            <select
                              className="border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-slate-50"
                              value={q.doc_type || "position"}
                              onChange={(e) =>
                                updateQ(q.id, { doc_type: e.target.value })
                              }
                              disabled={isLocked}
                            >
                              {SURVEY_DOC_TYPES.map((dt) => (
                                <option key={dt} value={dt}>
                                  {dt}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      key: "distribution",
      title: "Distribution",
      content: (
        <div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="font-medium text-slate-700 mb-3">Distribution</p>
            {localAudience === "candidate" ? (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-slate-500">
                  Candidate emails — one per line or comma-separated
                </label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                  rows={4}
                  placeholder={"candidate@example.com\nanother@example.com"}
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  disabled={isLocked}
                />
                {isDraft && (
                  <p className="text-xs text-slate-400">
                    Invitation links are minted when you publish on the Review
                    step.
                  </p>
                )}
                {isPublished && canPublish && !isLocked && (
                  <button
                    onClick={handleInvite}
                    disabled={publishing || !emailsText.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {publishing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    Send to additional candidates
                  </button>
                )}
                {inviteResult?.links?.length > 0 && (
                  <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                    <p className="text-xs font-medium text-slate-500">
                      Invitation links
                    </p>
                    {inviteResult.links.map((l) => (
                      <div
                        key={l.email}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-600 w-40 truncate text-xs">
                          {l.email}
                        </span>
                        <span className="text-slate-400 truncate flex-1 font-mono text-xs">
                          {l.link}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(l.link)}
                          className="text-slate-400 hover:text-blue-600 flex-shrink-0"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : localAudience === "user" ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Users className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1.5">
                    <p>
                      No links to send. Once published, this survey appears as a
                      red alert on the FieldIQ <strong>Today</strong> screen for
                      every user with access to this facility.
                    </p>
                    <p className="text-xs text-slate-400">
                      Respondents can keep editing their answers for as long as
                      the survey stays published. Close it from the survey list
                      to lock the answers and clear the alert.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 text-sm text-slate-600">
                <QrCode className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1.5">
                  <p>
                    Nothing to copy. Constituent surveys are{" "}
                    <strong>single use</strong> — a canvasser generates a
                    one-time QR code or shareable link from FieldIQ when they
                    reach the door.
                  </p>
                  <p className="text-xs text-slate-400">
                    Each link works once, expires after 7 days, and carries the
                    canvasser and the location it was issued at, so every
                    response is attributed automatically.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "review",
      title: "Review & Publish",
      content: (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="font-medium text-slate-700 mb-2">Summary</p>
            <div className="text-sm text-slate-600 space-y-1">
              <p>
                <span className="text-slate-400">Title:</span>{" "}
                {localTitle || "Untitled"}
              </p>
              <p>
                <span className="text-slate-400">Audience:</span>{" "}
                <span className="capitalize">{localAudience}</span>
              </p>
              <p>
                <span className="text-slate-400">Questions:</span>{" "}
                {localQuestions.filter((q) => q.label.trim()).length}
              </p>
              <p>
                <span className="text-slate-400">Collects:</span>{" "}
                {opensAt
                  ? `from ${new Date(`${opensAt}T00:00:00`).toLocaleDateString()}`
                  : "from publication"}{" "}
                {closesAt
                  ? `until ${new Date(`${closesAt}T00:00:00`).toLocaleDateString()}`
                  : "with no deadline"}
              </p>
              {localAudience === "candidate" && (
                <p>
                  <span className="text-slate-400">KB rows on approval:</span>{" "}
                  {kbPreview.length}
                </p>
              )}
              <p>
                <span className="text-slate-400">Status:</span>{" "}
                <span className="capitalize">{survey?.status || "draft"}</span>
              </p>
            </div>
          </div>
          {isDraft && canPublish && (
            <p className="text-xs text-slate-400">
              {localAudience === "candidate"
                ? "Publishing mints a single-use invitation link for each email entered in Distribution."
                : localAudience === "user"
                  ? "Publishing pushes this survey to FieldIQ immediately — it shows as a red alert on Today until you close it."
                  : "Publishing generates the public share link for QR codes and canvasser attribution."}
            </p>
          )}
          {inviteResult?.links?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-1.5 max-h-56 overflow-y-auto">
              <p className="text-xs font-medium text-slate-500">
                Invitation links
              </p>
              {inviteResult.links.map((l) => (
                <div key={l.email} className="flex items-center gap-2 text-sm">
                  <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-600 w-40 truncate text-xs">
                    {l.email}
                  </span>
                  <span className="text-slate-400 truncate flex-1 font-mono text-xs">
                    {l.link}
                  </span>
                  <button
                    onClick={() => navigator.clipboard.writeText(l.link)}
                    className="text-slate-400 hover:text-blue-600 flex-shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
        <button
          onClick={close}
          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <p className="flex-1 text-lg font-semibold text-slate-800 truncate">
          {localTitle || <span className="text-slate-300">New survey…</span>}
        </p>
        {survey?.status && (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
              SURVEY_STATUS_COLORS[survey.status] ||
              "bg-slate-100 text-slate-600"
            }`}
          >
            {survey.status}
          </span>
        )}
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          {err}
        </div>
      )}

      <WizardShell
        accent="blue"
        steps={steps}
        onStepChange={persistDraft}
        onFinish={finish}
        finishLabel={isDraft && canPublish ? "Publish Survey" : "Done"}
        busy={saving || publishing}
        onCancel={close}
        cancelLabel="Close"
      />
    </div>
  );
};

const parseAnswers = (raw) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return raw;
};

const formatAnswer = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) return value.join("; ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

// Who answered differs by audience: candidates come from their invitation,
// constituents carry the canvasser who captured them, users are the account.
const SURVEY_IDENTITY_COLUMNS = {
  candidate: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "office", label: "Office" },
  ],
  constituent: [{ key: "captured_by", label: "Captured by" }],
  user: [
    { key: "respondent_name", label: "Name" },
    { key: "respondent_email", label: "Email" },
  ],
};

const slugify = (t) =>
  String(t)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "survey";

const csvCell = (v) => {
  const s = v === undefined || v === null ? "" : String(v);
  // Quote when the value could otherwise break the row, and double any quote.
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
  const body = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  // BOM so Excel opens UTF-8 names correctly rather than mangling them.
  const blob = new Blob(["\uFEFF" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const SurveyResponses = ({ facility, kbRole, survey, onBack }) => {
  const [responses, setResponses] = useState([]);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedR, setExpandedR] = useState(new Set());
  const [approving, setApproving] = useState(new Set());
  const canApprove =
    survey.audience === "candidate" &&
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS.approver ?? 99);

  useEffect(() => {
    authFetch(`/surveys/${survey.id}/responses`)
      .then((d) => {
        setResponses(d?.responses || []);
        // The survey list does not carry schema, so without this every answer
        // was labelled with its raw question id.
        setSchema(Array.isArray(d?.schema) ? d.schema : []);
      })
      .catch(() => showToast("Failed to load survey responses."))
      .finally(() => setLoading(false));
  }, [survey.id]);

  // Question text in the order it was asked, plus any answer whose question
  // has since been removed from the survey — so nothing is silently dropped.
  const questionColumns = useMemo(() => {
    const cols = schema
      .filter((q) => q?.id)
      .map((q) => ({ id: q.id, label: q.label?.trim() || q.id }));
    const known = new Set(cols.map((c) => c.id));
    responses.forEach((r) => {
      Object.keys(parseAnswers(r.answers)).forEach((qid) => {
        if (!known.has(qid)) {
          known.add(qid);
          cols.push({ id: qid, label: qid, orphan: true });
        }
      });
    });
    return cols;
  }, [schema, responses]);

  const exportCsv = () => {
    const idCols = SURVEY_IDENTITY_COLUMNS[survey.audience] || [];
    const header = [
      "Submitted",
      ...idCols.map((c) => c.label),
      ...questionColumns.map((c) => c.label),
      ...(survey.audience === "candidate" ? ["Approved"] : []),
    ];
    const rows = responses.map((r) => {
      const answers = parseAnswers(r.answers);
      return [
        r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "",
        ...idCols.map((c) => r[c.key] ?? ""),
        ...questionColumns.map((c) => formatAnswer(answers[c.id])),
        ...(survey.audience === "candidate" ? [r.approved ? "yes" : "no"] : []),
      ];
    });
    downloadCsv(
      `${slugify(survey.title || "survey")}-responses-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      [header, ...rows],
    );
  };

  const toggleR = (id) =>
    setExpandedR((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const handleApprove = async (rid) => {
    setApproving((prev) => new Set([...prev, rid]));
    const res = await authFetch(
      `/surveys/${survey.id}/responses/${rid}/approve`,
      { method: "POST" },
    );
    setApproving((prev) => {
      const n = new Set(prev);
      n.delete(rid);
      return n;
    });
    if (!res?.error)
      setResponses((prev) =>
        prev.map((r) => (r.id === rid ? { ...r, approved: true } : r)),
      );
  };

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 truncate">
            {survey.title || "Untitled survey"} — Responses
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {responses.length} response{responses.length !== 1 ? "s" : ""}
            {survey.audience === "candidate" && canApprove
              ? " · Approve to sync to KB"
              : ""}
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={responses.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          title="Download every response as CSV"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>
      {responses.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">No responses yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {responses.map((r) => {
            const isExp = expandedR.has(r.id);
            const answers = parseAnswers(r.answers);
            return (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-slate-200"
              >
                <div className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    {survey.audience === "candidate" ? (
                      <>
                        <p className="font-medium text-slate-800 truncate">
                          {r.name || r.email || `Response #${r.id}`}
                        </p>
                        {r.email && r.name && (
                          <p className="text-xs text-slate-400">{r.email}</p>
                        )}
                        {r.office && (
                          <p className="text-xs text-slate-500">{r.office}</p>
                        )}
                      </>
                    ) : survey.audience === "user" ? (
                      <>
                        <p className="font-medium text-slate-800 truncate">
                          {r.respondent_name ||
                            r.respondent_email ||
                            `Response #${r.id}`}
                        </p>
                        {r.respondent_email && r.respondent_name && (
                          <p className="text-xs text-slate-400 truncate">
                            {r.respondent_email}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium text-slate-700">
                        Response #{r.id}
                        {r.captured_by && (
                          <span className="text-slate-400 font-normal">
                            {" "}
                            · PC {r.captured_by}
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(r.submitted_at).toLocaleString()}
                      {r.updated_at &&
                        new Date(r.updated_at).getTime() -
                          new Date(r.submitted_at).getTime() >
                          1000 && (
                          <span className="text-slate-400">
                            {" · edited "}
                            {new Date(r.updated_at).toLocaleString()}
                          </span>
                        )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.approved ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Approved
                      </span>
                    ) : (
                      canApprove && (
                        <button
                          onClick={() => handleApprove(r.id)}
                          disabled={approving.has(r.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {approving.has(r.id) ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Approve
                        </button>
                      )
                    )}
                    <button
                      onClick={() => toggleR(r.id)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md"
                    >
                      {isExp ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                {isExp && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {questionColumns.map((col) => {
                      const display = formatAnswer(answers[col.id]);
                      return (
                        <div key={col.id} className="px-4 py-3">
                          <p className="text-xs font-medium text-slate-500 mb-1">
                            {col.label}
                            {col.orphan && (
                              <span className="ml-1.5 text-slate-400 font-normal">
                                (question since removed)
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {display || (
                              <span className="italic text-slate-400">
                                Not answered
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const SurveysPanel = ({ facility, kbRole }) => {
  const [view, setView] = useState("list");
  const [activeSurvey, setActiveSurvey] = useState(null);

  // No blank POST here — the wizard creates the survey once it has a title,
  // so abandoned "New Survey" clicks no longer litter the list.
  const handleNew = () => {
    setActiveSurvey(null);
    setView("editor");
  };

  if (view === "editor")
    return (
      <SurveyEditor
        facility={facility}
        kbRole={kbRole}
        surveyId={activeSurvey?.id}
        onBack={() => {
          setActiveSurvey(null);
          setView("list");
        }}
      />
    );

  if (view === "responses")
    return (
      <SurveyResponses
        facility={facility}
        kbRole={kbRole}
        survey={activeSurvey}
        onBack={() => {
          setActiveSurvey(null);
          setView("list");
        }}
      />
    );

  return (
    <SurveyList
      facility={facility}
      kbRole={kbRole}
      onNew={handleNew}
      onEdit={(s) => {
        setActiveSurvey(s);
        setView("editor");
      }}
      onResponses={(s) => {
        setActiveSurvey(s);
        setView("responses");
      }}
    />
  );
};
